import {ChangeEvent, ComponentType, ReactElement, useState} from 'react'
import {I18nContextValues, Trans, withNamespaces} from 'react-i18next'
import {flowRight as compose} from 'lodash'
import {withStyles, StyledComponentProps, Grid} from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import messages from '../../../../../../../assets/messages'
import NotificationBar from '../../../../../../Common/NotificationBar'
import {AlertDialog, FullScreenDialog} from '../../../../../../Common/Dialog'
import SwitchButton from '../../../../../../Common/SwitchButton'
import {UpdateProviderBody, useUpdateProvider} from '../../hooks/useUpdateProvider'
import {StartProvidingForm} from '../StartProvidingForm'
import {styles} from './styles'

type IsProvidingInputBaseProps = {
  accountId: number
  value: boolean
  shouldValidatePassword: boolean
  disabled: boolean
  onUpdated: () => Promise<void>
}

type IsProvidingInputProps = StyledComponentProps & I18nContextValues & IsProvidingInputBaseProps

const Component = (props: IsProvidingInputProps): ReactElement => {
  const {t, classes = {}, accountId, value, shouldValidatePassword, disabled, onUpdated} = props

  const [isStopProvidingDialogShown, setStopProvidingDialogVisibility] = useState<boolean>(false)
  const [isStartProvidingDialogShown, setStartProvidingDialogVisibility] = useState<boolean>(false)

  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleUpdateSuccess = async (body: UpdateProviderBody): Promise<void> => {
    await onUpdated()
    setStopProvidingDialogVisibility(false)
    setErrorMessage(null)

    const message = body.isProviding ? messages.brokereeEnableCopyTradingSuccess : messages.brokereeDisableCopyTradingSuccess
    setSuccessMessage(t(message.i18nKey, message.defaults))
  }

  const handleUpdateError = (): void => {
    // TODO: update error message
    setErrorMessage(t(messages.somethingWrongError.i18nKey, messages.somethingWrongError.defaults))
    setSuccessMessage(null)
  }

  const {updateProvider, loading} = useUpdateProvider(accountId, {onError: handleUpdateError, onSuccess: handleUpdateSuccess})

  const handleProvidingChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (loading) return

    const nextIsProviding = e.target.checked

    if (!nextIsProviding) setStopProvidingDialogVisibility(true)
    else if (shouldValidatePassword) setStartProvidingDialogVisibility(true)
    else updateProvider({isProviding: true})
  }

  const handleStopDialogAgree = async (): Promise<void> => {
    await updateProvider({isProviding: false})
  }

  const handleDialogClose = (): void => {
    setStopProvidingDialogVisibility(false)
    setStartProvidingDialogVisibility(false)
  }

  const handleNotificationClose = (): void => {
    setSuccessMessage(null)
  }

  const handleProviderEnabled = async (): Promise<void> => {
    handleDialogClose()
    setSuccessMessage(t(messages.brokereeEnableCopyTradingSuccess.i18nKey, messages.brokereeEnableCopyTradingSuccess.defaults))
    await onUpdated()
  }

  return (
    <>
      <Grid item sm={10} xs={12}>
        <SwitchButton
          id="provideCopyTrading"
          iOSSwitch
          checked={value}
          disabled={disabled}
          onChange={handleProvidingChange}
          label={t(messages.provideCopyTrading.i18nKey, messages.provideCopyTrading.defaults)}
        />
      </Grid>

      {successMessage != null ?
        (<Grid item sm={10} xs={12}>
          <NotificationBar status="success" onClose={handleNotificationClose}>
            {successMessage}
          </NotificationBar>
        </Grid>)
        : null}

      <AlertDialog
        open={isStopProvidingDialogShown}
        onClose={handleDialogClose}
        onAgree={handleStopDialogAgree}
        onDisagree={handleDialogClose}
        disagreeText={t(messages.cancel.i18nKey, messages.cancel.defaults)}
        agreeText={t(messages.stopProviding.i18nKey, messages.stopProviding.defaults)}
        title = {
          <Typography variant='body1' className={classes.alertTitle}>
            <Trans {...messages.stopProvidingTitle} components={[<span className={classes.errorMessage}>stopProviding</span>]}/>
          </Typography>
        }
        buttonLoading={loading}
        buttonStatus={errorMessage != null ? 'failure' : null}
      >
        <>
          <Typography variant="body1">
            <Trans {...messages.stopProvidingText1} />
          </Typography><br />
          <Typography variant="body1">
            <Trans {...messages.stopProvidingText2}/>
          </Typography>
          {errorMessage != null ? <NotificationBar status="error">{errorMessage}</NotificationBar> : null}
        </>
      </AlertDialog>

      <FullScreenDialog
          fullScreen
          open={isStartProvidingDialogShown}
          onClose={handleDialogClose}
        >
        <StartProvidingForm accountId={accountId} onProviderEnabled={handleProviderEnabled} />
      </FullScreenDialog>
    </>
  )
}

export const IsProvidingInput: ComponentType<IsProvidingInputBaseProps> = compose(
  withStyles(styles),
  withNamespaces()
)(Component)

